import { useEffect, useRef, useState } from "react";
import { Box, Button, Chip, Typography } from "@mui/material";
import {
  CheckCircle as CheckIcon,
  ErrorOutline as ErrorIcon,
} from "@mui/icons-material";
import { useFormContext, useWatch } from "react-hook-form";
import {
  classifyBankVerifyError,
  isPlausibleAccountNumber,
  isValidIfscFormat,
  verifyBank,
} from "../../../services/bankVerify";

type Props = { branchIndex: number };

type Status = "idle" | "verifying" | "verified" | "failed" | "error";

const DEBOUNCE_MS = 400;

export function BankVerificationEffect({ branchIndex }: Props) {
  const { control, setValue } = useFormContext();
  const accountNumber = useWatch({
    control,
    name: `branches.${branchIndex}.facility_account_number`,
  }) as string | undefined;
  const ifsc = useWatch({
    control,
    name: `branches.${branchIndex}.facility_ifsc_code`,
  }) as string | undefined;
  const persistedStatus = useWatch({
    control,
    name: `branches.${branchIndex}.bank_verification_status`,
  }) as string | undefined;
  const persistedName = useWatch({
    control,
    name: `branches.${branchIndex}.bank_verified_name`,
  }) as string | undefined;

  const [status, setStatus] = useState<Status>(
    persistedStatus === "verified" ? "verified" : "idle",
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const verifiedPairsRef = useRef<Map<string, string>>(
    new Map(
      persistedStatus === "verified" && persistedName
        ? [
            [
              `${(accountNumber ?? "").trim()}|${(ifsc ?? "").trim().toUpperCase()}`,
              persistedName,
            ],
          ]
        : [],
    ),
  );
  const currentPairRef = useRef<string | null>(
    persistedStatus === "verified"
      ? `${(accountNumber ?? "").trim()}|${(ifsc ?? "").trim().toUpperCase()}`
      : null,
  );
  const inFlightForRef = useRef<string | null>(null);

  useEffect(() => {
    const acc = (accountNumber ?? "").trim();
    const ifs = (ifsc ?? "").trim().toUpperCase();
    const pair = `${acc}|${ifs}`;

    if (currentPairRef.current && currentPairRef.current !== pair) {
      currentPairRef.current = null;
      inFlightForRef.current = null;
      setStatus("idle");
      setErrorMessage(null);
      setValue(
        `branches.${branchIndex}.account_holder_name`,
        "",
        { shouldDirty: false, shouldValidate: false },
      );
      setValue(
        `branches.${branchIndex}.bank_verification_status`,
        "",
        { shouldDirty: false },
      );
      setValue(
        `branches.${branchIndex}.bank_verified_name`,
        "",
        { shouldDirty: false },
      );
    }

    if (!isPlausibleAccountNumber(acc)) return;
    if (!isValidIfscFormat(ifs)) return;

    const cachedName = verifiedPairsRef.current.get(pair);
    if (cachedName) {
      currentPairRef.current = pair;
      inFlightForRef.current = null;
      setStatus("verified");
      setErrorMessage(null);
      setValue(
        `branches.${branchIndex}.account_holder_name`,
        cachedName,
        { shouldDirty: true, shouldValidate: true },
      );
      setValue(
        `branches.${branchIndex}.bank_verification_status`,
        "verified",
        { shouldDirty: true },
      );
      setValue(
        `branches.${branchIndex}.bank_verified_name`,
        cachedName,
        { shouldDirty: true },
      );
      return;
    }
    // Rehydrated verified pair with no persisted bank_verified_name — Map
    // couldn't be seeded, but currentPairRef was, so this skips a mount-time
    // redundant Cashfree call.
    if (currentPairRef.current === pair) return;
    if (inFlightForRef.current === pair) return;

    const timer = setTimeout(() => {
      inFlightForRef.current = pair;
      setStatus("verifying");
      setErrorMessage(null);
      void (async () => {
        try {
          const result = await verifyBank({
            account_number: acc,
            ifsc: ifs,
          });
          if (inFlightForRef.current !== pair) return;
          if (result.valid && result.nameAtBank) {
            verifiedPairsRef.current.set(pair, result.nameAtBank);
            currentPairRef.current = pair;
            setStatus("verified");
            setValue(
              `branches.${branchIndex}.account_holder_name`,
              result.nameAtBank,
              { shouldDirty: true, shouldValidate: true },
            );
            setValue(
              `branches.${branchIndex}.bank_verification_status`,
              "verified",
              { shouldDirty: true },
            );
            setValue(
              `branches.${branchIndex}.bank_verified_name`,
              result.nameAtBank,
              { shouldDirty: true },
            );
          } else {
            setStatus("failed");
            setErrorMessage(
              "Bank account verification failed. Please check the account number and IFSC.",
            );
            setValue(
              `branches.${branchIndex}.bank_verification_status`,
              "failed",
              { shouldDirty: true },
            );
          }
        } catch (err) {
          if (inFlightForRef.current !== pair) return;
          const info = classifyBankVerifyError(err);
          if (info.kind === "unavailable") {
            setStatus("idle");
            return;
          }
          setStatus("error");
          setErrorMessage(info.message);
        } finally {
          if (inFlightForRef.current === pair) inFlightForRef.current = null;
        }
      })();
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [accountNumber, ifsc, branchIndex, setValue]);

  const handleRetry = () => {
    currentPairRef.current = null;
    inFlightForRef.current = null;
    setStatus("idle");
    setErrorMessage(null);
    setValue(
      `branches.${branchIndex}.bank_verification_status`,
      "",
      { shouldDirty: false },
    );
  };

  if (status === "idle") return null;

  return (
    <Box sx={{ mt: 1, mb: 1.5 }}>
      {status === "verifying" && (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Verifying bank details…
        </Typography>
      )}
      {status === "verified" && (
        <Chip
          size="small"
          icon={<CheckIcon sx={{ fontSize: 16 }} />}
          label={`Verified · ${persistedName ?? "Account holder"}`}
          sx={{
            bgcolor: "#E6F4EA",
            color: "#0F5132",
            fontWeight: 600,
            fontSize: 12,
            "& .MuiChip-icon": { color: "#0F5132" },
          }}
        />
      )}
      {(status === "failed" || status === "error") && errorMessage && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ErrorIcon sx={{ fontSize: 18, color: "error.main" }} />
          <Typography variant="caption" sx={{ color: "error.main" }}>
            {errorMessage}
          </Typography>
          {status === "error" && (
            <Button
              size="small"
              variant="text"
              onClick={handleRetry}
              sx={{ ml: 1, textTransform: "none", fontSize: 12 }}
            >
              Retry
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}
